/**
 * Full Bleed site header.
 *
 * Ports the prototype's header behaviour (apps/full-bleed/src/menu-options)
 * into a section-owned lifecycle, per LIQUID-MIGRATION.md: every listener is
 * bound through an AbortController so `shopify:section:load` /
 * `shopify:section:unload` mount and unmount cleanly in the theme editor.
 *
 * Responsibilities:
 *   - native <dialog> drawer open/close, with animation-aware teardown
 *   - drawer cart count kept in sync with Horizon's own cart bubble
 *   - duplicated-span hover label motion on the nav links
 *   - html[data-menu-option] = 'centered' | 'right' (assets/menu-options.css)
 *   - --header-height / --header-group-height / --ph-header-offset metrics,
 *     which the rest of the theme reads for sticky offsets and hero sizing
 *
 * Deliberately dependency-free: the drawer must keep working even if a
 * module on the import map fails to resolve.
 */

// A pure width check, not touch-based: an iPad (or any touch laptop/tablet
// with a trackpad or mouse attached) can report `hover: hover, pointer: fine`
// just like a desktop once a pointing device is connected, so a touch-based
// clause here is unreliable for deciding the *layout* — narrow desktop
// windows and wide touch tablets both need to land on the right side of
// this consistently. 990px matches Horizon's own desktop breakpoint
// (assets/base.css's `min-width: 990px`), so nothing sits in a gap between
// the two.
const COMPACT_QUERY = '(max-width: 989px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
// :not(.ph-header__dropdown-editorial) — the big editorial image link is the
// one link this swap genuinely doesn't belong on: its contents (the image
// frame, ornaments, CTA) would get stuffed into a `.ph-header__label-stack`
// built for a short text label, which clips it (`overflow: hidden` sized to
// that content) and then fades it out on hover as the "original" half of a
// swap that has no real "copy" half to show. Plain dropdown text links
// (`.ph-header__dropdown-link`) get the same swap as the top-level trigger
// links — nothing about them makes it pointless there too.
const NAV_LINK_SELECTOR =
  '.ph-header__nav a:not(.ph-header__dropdown-editorial), ' + '.menu-nav a:not(.ph-header__dropdown-editorial)';
const DRAWER_CLOSE_FALLBACK_MS = 500;

/* ------------------------------------------------------------------ *
 * Menu option (layout variant)
 * ------------------------------------------------------------------ */

let menuOptionInitialized = false;

function initMenuOption() {
  if (menuOptionInitialized) return;
  menuOptionInitialized = true;

  const compact = window.matchMedia(COMPACT_QUERY);
  const apply = () => {
    document.documentElement.dataset.menuOption = compact.matches ? 'right' : 'centered';
  };

  apply();
  compact.addEventListener('change', apply);
}

/* ------------------------------------------------------------------ *
 * Hover label motion
 * ------------------------------------------------------------------ */

/**
 * Wraps each nav link's contents in the stacked label pair the hover swap
 * animates. Links rendered by sections/header.liquid already ship the stack;
 * this covers links injected by other sections or by the theme editor.
 *
 * @param {ParentNode} root
 */
export function enhanceMenuLabelMotion(root = document) {
  for (const control of root.querySelectorAll(NAV_LINK_SELECTOR)) {
    if (control.classList.contains('has-menu-label-motion')) continue;

    if (!control.querySelector('.ph-header__label-stack')) {
      const stack = document.createElement('span');
      const label = document.createElement('span');

      stack.className = 'ph-header__label-stack';
      label.className = 'ph-header__label ph-header__label--original';

      while (control.firstChild) label.append(control.firstChild);

      const copy = /** @type {HTMLElement} */ (label.cloneNode(true));
      copy.className = 'ph-header__label ph-header__label--copy';
      copy.setAttribute('aria-hidden', 'true');

      stack.append(label, copy);
      control.append(stack);
    }

    control.classList.add('has-menu-label-motion');
  }
}

/* ------------------------------------------------------------------ *
 * Header metrics
 * ------------------------------------------------------------------ */

/**
 * Publishes the measurements the rest of the theme reads:
 *   --header-height        the fixed bar's own height
 *   --header-group-height  flow height of #header-group (the fixed bar
 *                          contributes 0, announcement bars do not)
 *   --ph-header-offset     how far the bar is pushed down by the header-group
 *                          content above it, released as that content scrolls
 *
 * @param {HTMLElement} header - the [data-ph-header] element
 * @param {AbortSignal} signal
 * @returns {() => void} cleanup
 */
function observeHeaderMetrics(header, signal) {
  const headerGroup = document.querySelector('#header-group');
  const section = header.closest('.shopify-section') ?? header.parentElement;
  let rafId = null;
  let aboveHeight = 0;

  const setHeaderHeight = () => {
    const height = Math.round(header.getBoundingClientRect().height);
    if (height > 0) document.body.style.setProperty('--header-height', `${height}px`);
  };

  const setGroupHeight = () => {
    if (!headerGroup) return;

    let total = 0;
    for (const child of headerGroup.children) {
      if (child instanceof HTMLElement) total += child.offsetHeight;
    }
    document.body.style.setProperty('--header-group-height', `${Math.round(total)}px`);
  };

  const setAboveHeight = () => {
    aboveHeight = 0;
    if (headerGroup && section instanceof HTMLElement) {
      for (const child of headerGroup.children) {
        if (child === section) break;
        if (child instanceof HTMLElement) aboveHeight += child.offsetHeight;
      }
    }
    updateOffset();
  };

  const updateOffset = () => {
    const offset = aboveHeight === 0 ? 0 : Math.max(0, aboveHeight - window.scrollY);
    header.style.setProperty('--ph-header-offset', `${offset}px`);
  };

  const onScroll = () => {
    if (aboveHeight === 0 || rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      updateOffset();
    });
  };

  const measure = () => {
    setHeaderHeight();
    setGroupHeight();
    setAboveHeight();
  };

  const resizeObserver = new ResizeObserver(measure);
  resizeObserver.observe(header);
  if (headerGroup) {
    for (const child of headerGroup.children) {
      if (child instanceof HTMLElement) resizeObserver.observe(child);
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true, signal });
  window.addEventListener('resize', measure, { passive: true, signal });
  measure();

  return () => {
    resizeObserver.disconnect();
    if (rafId !== null) cancelAnimationFrame(rafId);
  };
}

/* ------------------------------------------------------------------ *
 * Section lifecycle
 * ------------------------------------------------------------------ */

/**
 * A links-only dropdown (`.ph-header__dropdown.no-editorial`) is sized to its
 * own links, so it's usually wider than the nav word it hangs from. Publish
 * its width on the nav item as `--ph-dropdown-width` so the trigger's pink
 * hover tab can match it (component-header.css). The panel keeps its layout
 * while hidden (opacity/visibility only), so it can be measured any time.
 *
 * @param {HTMLElement} root - the section wrapper
 * @param {AbortSignal} signal
 */
function syncDropdownTabWidths(root, signal) {
  const panels = root.querySelectorAll('.ph-header__dropdown.no-editorial');
  if (!panels.length) return;

  const publish = (panel) => {
    const item = panel.closest('.ph-header__nav-item');
    if (item instanceof HTMLElement) item.style.setProperty('--ph-dropdown-width', `${panel.getBoundingClientRect().width}px`);
  };

  const observer = new ResizeObserver((entries) => entries.forEach((entry) => publish(entry.target)));
  panels.forEach((panel) => {
    publish(panel);
    observer.observe(panel);
  });
  signal.addEventListener('abort', () => observer.disconnect());
}

/**
 * @param {HTMLElement} root - the section wrapper
 * @returns {() => void} cleanup
 */
function mountHeader(root) {
  const controller = new AbortController();
  const { signal } = controller;

  const header = /** @type {HTMLElement | null} */ (root.querySelector('[data-ph-header]'));
  const drawer = /** @type {HTMLDialogElement | null} */ (root.querySelector('[data-ph-drawer]'));

  if (!header) return () => controller.abort();

  initMenuOption();
  enhanceMenuLabelMotion(root);
  const stopMetrics = observeHeaderMetrics(header, signal);
  syncDropdownTabWidths(root, signal);

  /* ---- Drawer ---- */

  const triggers = root.querySelectorAll('[data-ph-header-trigger]');
  const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
  let closing = false;
  let previousOverflow = '';

  const setExpanded = (value) => {
    for (const trigger of triggers) trigger.setAttribute('aria-expanded', String(value));
  };

  const openDrawer = () => {
    if (!drawer || drawer.open) return;
    previousOverflow = document.body.style.overflow;
    // Mobile ('right'): a non-modal `.show()`, not `.showModal()` — the
    // header bar sits above this drawer now (component-header.css), not
    // covered by it, specifically so its own search/cart/account controls
    // stay usable while the drawer's open. `showModal()` would undo that:
    // a modal dialog's native backdrop covers the *entire* viewport
    // (including the header strip) regardless of the dialog's own size or
    // position, and intercepts every click there, so header buttons would
    // visually show through but never actually receive the click. Desktop
    // ('centered') keeps the true modal — it's a centered card over a
    // dimmed page, where that inert-background behavior is exactly right.
    if (document.documentElement.dataset.menuOption === 'right') drawer.show();
    else drawer.showModal();
    document.body.style.overflow = 'hidden';
    setExpanded(true);
    header.classList.add('ph-header--menu-open');
  };

  /** Set while the closing animation runs; cleared by the first thing to finish it. */
  let pendingClose = null;

  const closeDrawer = () => {
    if (!drawer || !drawer.open || closing) return;
    closing = true;
    setExpanded(false);
    drawer.classList.add('is-closing');

    const finish = () => {
      if (pendingClose !== finish) return;
      pendingClose = null;
      closing = false;
      drawer.classList.remove('is-closing');
      drawer.close();
      document.body.style.overflow = previousOverflow;
      header.classList.remove('ph-header--menu-open');
    };

    pendingClose = finish;

    if (reducedMotion.matches) {
      finish();
      return;
    }

    // Guard against the animation never running (a stylesheet that disables
    // it, a browser that skips animations on a closing dialog, a hidden tab).
    setTimeout(finish, DRAWER_CLOSE_FALLBACK_MS);
  };

  if (drawer) {
    for (const trigger of triggers) {
      trigger.addEventListener(
        'click',
        () => {
          if (drawer.open) closeDrawer();
          else openDrawer();
        },
        { signal }
      );
    }

    // The closing animation finishes the close; bubbled animations from the
    // panel's contents are ignored.
    drawer.addEventListener(
      'animationend',
      (event) => {
        if (event.target === drawer) pendingClose?.();
      },
      { signal }
    );

    // Light-dismiss for the true-modal (desktop) case: a click that lands
    // on the dialog element itself is a click on its native backdrop,
    // since the panel fills the dialog.
    drawer.addEventListener(
      'click',
      (event) => {
        if (event.target === drawer) closeDrawer();
      },
      { signal }
    );

    // Light-dismiss for the non-modal (mobile) case, which has no native
    // backdrop to catch the click above: anything outside the drawer's own
    // box that isn't the trigger itself (its own handler already toggles)
    // closes the drawer. This is also what lets a click on a header button
    // — search, cart, account — close the menu without swallowing the
    // click, since it never calls `preventDefault()`.
    document.addEventListener(
      'click',
      (event) => {
        if (!drawer.open) return;
        const target = /** @type {Element | null} */ (event.target);
        if (!(target instanceof Element)) return;
        if (drawer.contains(target) || target.closest('[data-ph-header-trigger]')) return;
        closeDrawer();
      },
      { signal }
    );

    // Escape: run the closing animation instead of the instant native
    // close. The native `cancel` event only fires for a true modal
    // (`showModal()`); the `keydown` listener is what covers the non-modal
    // (mobile) case, and is a harmless no-op duplicate on top of `cancel`
    // for the modal case (closeDrawer() already guards against re-entry).
    drawer.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        closeDrawer();
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && drawer.open) closeDrawer();
      },
      { signal }
    );

    // Any navigation or dismissing control inside the drawer closes it.
    drawer.addEventListener(
      'click',
      (event) => {
        const target = /** @type {Element | null} */ (event.target);
        if (!(target instanceof Element)) return;
        if (target.closest('a[href], [data-ph-drawer-dismiss]')) closeDrawer();
      },
      { signal }
    );
  }

  /* ---- Nav dropdown (mega-menu) ----
   * CSS alone opens this on real mouse hover/keyboard focus
   * (`:hover`/`:focus-within` in component-header.css). A trigger like
   * "Shop" is both a real link and a dropdown trigger at once; an earlier
   * version of this file also intercepted the first click/tap to reveal
   * the dropdown instead of following the link (requiring a second
   * activation to actually navigate) for touch/click-only users who never
   * get a hover state. That traded away "click always goes where the link
   * says" — by request, this now always navigates on click, on any
   * device; the dropdown is preview-only, available via hover/focus.
   */

  /* ---- Cart count ---- */

  // Horizon's cart-icon.js owns the authoritative bubble; the drawer's
  // "Shopping bag (n)" label mirrors it rather than re-fetching the cart.
  const cartCounts = root.querySelectorAll('[data-ph-cart-count]');
  const bubbleCount = root.querySelector('.cart-bubble__text-count');

  const syncCartCount = (value) => {
    let count = value;

    if (typeof count !== 'number') {
      count = parseInt(bubbleCount?.textContent?.trim() || '0', 10);
      if (Number.isNaN(count)) count = 0;
    }

    for (const element of cartCounts) {
      element.textContent = String(count);
      element.hidden = count === 0;
    }
  };

  let bubbleObserver = null;

  if (cartCounts.length) {
    if (bubbleCount) {
      bubbleObserver = new MutationObserver(() => syncCartCount());
      bubbleObserver.observe(bubbleCount, { childList: true, characterData: true, subtree: true });
    }

    // Themes and apps that broadcast their own cart payload.
    for (const eventName of ['cart:updated', 'cart:update', 'cart:refresh']) {
      document.addEventListener(
        eventName,
        (event) => {
          const count = /** @type {any} */ (event)?.detail?.cart?.item_count;
          syncCartCount(typeof count === 'number' ? count : undefined);
        },
        { signal }
      );
    }

    window.addEventListener('pageshow', () => syncCartCount(), { signal });
    syncCartCount();
  }

  return () => {
    controller.abort();
    stopMetrics();
    bubbleObserver?.disconnect();
    if (drawer?.open) {
      drawer.classList.remove('is-closing');
      drawer.close();
      // Only this instance's lock is released; another component may own one.
      document.body.style.overflow = previousOverflow;
      header.classList.remove('ph-header--menu-open');
    }
  };
}

/* ------------------------------------------------------------------ *
 * Boot
 * ------------------------------------------------------------------ */

/** @type {Map<HTMLElement, () => void>} */
const instances = new Map();

/** @param {HTMLElement} root */
function init(root) {
  if (instances.has(root)) return;
  instances.set(root, mountHeader(root));
}

/** @param {HTMLElement} root */
function destroy(root) {
  instances.get(root)?.();
  instances.delete(root);
}

/** @param {Element} element */
function sectionRootFor(element) {
  return /** @type {HTMLElement} */ (element.closest('.shopify-section') ?? element.parentElement ?? element);
}

for (const element of document.querySelectorAll('[data-ph-header]')) {
  init(sectionRootFor(element));
}

document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  if (target.querySelector?.('[data-ph-header]')) init(target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  if (instances.has(target)) destroy(target);
});

// Gate the drawer's entrance animation until after first paint so the dialog
// never animates during hydration.
requestAnimationFrame(() => document.documentElement.classList.add('ph-page-ready'));
